import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Setup from "./pages/Setup";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Deliveries from "./pages/Deliveries";
import DeliveryDetail from "./pages/DeliveryDetail";
import Customers from "./pages/Customers";
import Excavators from "./pages/Excavators";
import Parts from "./pages/Parts";
import Invoices from "./pages/Invoices";
import Financials from "./pages/Financials";
import Settings from "./pages/Settings";
import Users from "./pages/Users";
import PDRs from "./pages/PDRs";
import JobOrders from "./pages/JobOrders";
import Layout from "./components/Layout";
import "./App.css";

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { user, loading, needsSetup, hasPermission } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/setup" element={<Setup />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="jobs"
              element={
                <ProtectedRoute requiredPermission="jobs">
                  <Jobs />
                </ProtectedRoute>
              }
            />
            <Route
              path="jobs/:jobId"
              element={
                <ProtectedRoute requiredPermission="jobs">
                  <JobDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="deliveries"
              element={
                <ProtectedRoute requiredPermission="deliveries">
                  <Deliveries />
                </ProtectedRoute>
              }
            />
            <Route
              path="deliveries/:deliveryId"
              element={
                <ProtectedRoute requiredPermission="deliveries">
                  <DeliveryDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="customers"
              element={
                <ProtectedRoute requiredPermission="customers">
                  <Customers />
                </ProtectedRoute>
              }
            />
            <Route
              path="excavators"
              element={
                <ProtectedRoute requiredPermission="excavators">
                  <Excavators />
                </ProtectedRoute>
              }
            />
            <Route
              path="parts"
              element={
                <ProtectedRoute requiredPermission="parts">
                  <Parts />
                </ProtectedRoute>
              }
            />
            <Route
              path="invoices"
              element={
                <ProtectedRoute requiredPermission="invoices">
                  <Invoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="financials"
              element={
                <ProtectedRoute requiredPermission="financials">
                  <Financials />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute requiredPermission="settings">
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <ProtectedRoute requiredPermission="users">
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="pdrs"
              element={
                <ProtectedRoute requiredPermission="pdrs">
                  <PDRs />
                </ProtectedRoute>
              }
            />
            <Route
              path="job-orders"
              element={
                <ProtectedRoute requiredPermission="job_orders">
                  <JobOrders />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
